/**
 * Mock of `/api/ai` for `VITE_MOCK=1` (the real route is
 * `apps/app/src/routes/ai.ts`). Off by default like the API. The answers are
 * canned: no model is called. In memory only.
 */
import type {
  AiBrandProfileResponse,
  AiBrandQuestion,
  AiBrandValue,
  AiLanguage,
  AiPrompt,
  AiSettings,
  AiSettingsResponse,
  AiSubjectSuggestion,
} from '../../types/ai';
import type { Role } from '../../types/api';
import { handleMockAiTasks, resetMockAiTasks } from './aiTasks';

const DEFAULT_SETTINGS: AiSettings = {
  enabled: false,
  dailyRequestCap: 30,
  brand: {
    tone: '',
    doWords: [],
    dontWords: [],
    products: [],
    languages: ['en'],
    signOff: '',
    rules: [],
  },
  updatedAt: null,
};

/** A short questionnaire in the shape of the real one (the API sends the full list). */
const MOCK_BRAND_QUESTIONS: AiBrandQuestion[] = [
  {
    id: 'companyName',
    section: 'company',
    type: 'text',
    question: 'What is the company called?',
    help: 'The name as it should appear in mails.',
    maxLength: 80,
    countsInProgress: true,
  },
  {
    id: 'tone',
    section: 'voice',
    type: 'text',
    question: 'How should the company sound?',
    help: 'In your own words.',
    maxLength: 400,
    countsInProgress: true,
  },
  {
    id: 'languages',
    section: 'voice',
    type: 'choice',
    question: 'Which languages do you write mails in?',
    help: 'Subject lines are written in each of them.',
    multiple: true,
    options: [
      { value: 'en', label: 'English' },
      { value: 'da', label: 'Danish' },
    ],
    countsInProgress: false,
  },
  {
    id: 'emojiUse',
    section: 'voice',
    type: 'choice',
    question: 'How are emojis used?',
    help: 'Pick one.',
    options: [
      { value: 'none', label: 'No emojis' },
      { value: 'sparing', label: 'Sparingly' },
    ],
    countsInProgress: true,
  },
  {
    id: 'dontWords',
    section: 'voice',
    type: 'list',
    question: 'Which words must never be used?',
    help: 'One per line.',
    maxLength: 80,
    maxItems: 20,
    countsInProgress: true,
  },
  {
    id: 'productList',
    section: 'products',
    type: 'products',
    question: 'What are your products?',
    help: 'For each: what it is, facts, claims never to make.',
    countsInProgress: true,
  },
];
const MOCK_BRAND_SECTIONS = [
  { id: 'company', title: 'Company' },
  { id: 'voice', title: 'Voice' },
  { id: 'products', title: 'Products' },
];
const EMPTY_PROFILE: Record<string, AiBrandValue> = {
  companyName: '',
  tone: '',
  languages: ['en'],
  emojiUse: '',
  dontWords: [],
  productList: [],
};
let brandProfile: Record<string, AiBrandValue> = structuredClone(EMPTY_PROFILE);

function brandProfileBody(): AiBrandProfileResponse {
  let answered = 0;
  let total = 0;
  for (const question of MOCK_BRAND_QUESTIONS) {
    if (!question.countsInProgress) continue;
    total += 1;
    const v = brandProfile[question.id];
    if (Array.isArray(v) ? v.length > 0 : v !== '' && v !== undefined) answered += 1;
  }
  const filled = Object.entries(brandProfile)
    .filter(([, v]) => (Array.isArray(v) ? v.length > 0 : v !== ''))
    .map(([k, v]) => `- ${k}: ${JSON.stringify(v)}`);
  return {
    sections: MOCK_BRAND_SECTIONS,
    questions: MOCK_BRAND_QUESTIONS,
    profile: brandProfile,
    progress: { answered, total },
    promptText: `## Brand profile\n${filled.join('\n')}`,
  };
}

let settings: AiSettings = structuredClone(DEFAULT_SETTINGS);
let available = true;
let requests = 0;
let prompts: AiPrompt[] = [];
let nextPrompt = 1;
let nextTemplate = 1;

/** Resets the mock to a company that never switched the assistant on. */
export function resetMockAi(): void {
  settings = structuredClone(DEFAULT_SETTINGS);
  brandProfile = structuredClone(EMPTY_PROFILE);
  available = true;
  requests = 0;
  prompts = [];
  nextPrompt = 1;
  nextTemplate = 1;
  resetMockAiTasks();
}

/** Lets a test see an environment without the Workers AI binding. */
export function setMockAiAvailable(value: boolean): void {
  available = value;
}

/** Lets a test start with the assistant already switched on. */
export function setMockAiEnabled(value: boolean): void {
  settings = { ...settings, enabled: value };
}

function json(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

function settingsBody(): AiSettingsResponse {
  return {
    ...settings,
    available,
    usage: {
      day: new Date().toISOString().slice(0, 10),
      requests,
      neurons: requests * 120,
      neuronBudget: 8000,
      freeNeuronsPerDay: 10000,
    },
  };
}

/** The same order as the API: switch, binding, then the daily cap. */
function gate(): Response | null {
  if (!settings.enabled) {
    return json(
      {
        error: 'ai_disabled',
        message:
          'The AI assistant is switched off. An admin can turn it on under Settings > AI assistant.',
      },
      403,
    );
  }
  if (!available) {
    return json(
      {
        error: 'ai_not_configured',
        message: 'The AI assistant is not available in this environment.',
      },
      503,
    );
  }
  if (requests >= settings.dailyRequestCap) {
    return json(
      {
        error: 'ai_daily_limit',
        message: "Today's AI limit for this company is used up. It resets at 00:00 UTC.",
      },
      429,
    );
  }
  requests += 1;
  return null;
}

const RESPONSE_LANGUAGES: readonly AiLanguage[] = ['en', 'da', 'de', 'fr', 'it', 'nl'];

/** Returns a response for an `/ai` path, or `null` when the path is not one. */
export function handleMockAi(
  method: string,
  pathname: string,
  body: unknown,
  role: Role,
): Response | null {
  if (!pathname.startsWith('/ai/')) return null;
  const path = pathname.slice('/ai'.length);
  const input = (body ?? {}) as Record<string, unknown>;
  const task = handleMockAiTasks(method, path, body, role, {
    enabled: settings.enabled,
    available,
  });
  if (task) return task;

  if (path === '/settings' && method === 'GET') return json(settingsBody());
  if (path === '/settings' && method === 'PUT') {
    if (role !== 'admin') return json({ error: 'forbidden' }, 403);
    const next = body as (Omit<AiSettings, 'brand'> & { brand?: AiSettings['brand'] }) | undefined;
    if (
      !next ||
      !Number.isInteger(next.dailyRequestCap) ||
      next.dailyRequestCap < 1 ||
      next.dailyRequestCap > 200
    ) {
      return json({ error: 'invalid_request', message: 'The daily limit must be 1 to 200.' }, 400);
    }
    settings = {
      enabled: next.enabled,
      dailyRequestCap: next.dailyRequestCap,
      brand: next.brand ?? settings.brand,
      updatedAt: Date.now(),
    };
    return json(settings);
  }

  // --- brand profile (works with the assistant off; never changes the switch) ---
  if (path === '/brand-profile' && method === 'GET') return json(brandProfileBody());
  if (path === '/brand-profile' && method === 'PUT') {
    if (role === 'viewer') return json({ error: 'forbidden' }, 403);
    const changes = (input as { profile?: Record<string, AiBrandValue> }).profile;
    if (!changes) return json({ error: 'invalid_request' }, 400);
    brandProfile = { ...brandProfile, ...changes };
    return json(brandProfileBody());
  }

  // --- prompt library (works with the assistant off) ---
  if (path === '/skills/newsletter/references' && method === 'GET') {
    return json({
      references: [
        {
          id: 'cmp-sent-1',
          name: 'Halloween 2026 (EN)',
          sentAt: Date.now() - 86_400_000,
          language: 'en',
        },
        {
          id: 'cmp-sent-2',
          name: 'Halloween 2026 (DE)',
          sentAt: Date.now() - 90_000_000,
          language: 'de',
        },
      ],
    });
  }
  if (path === '/prompts' && method === 'GET') {
    return json({ prompts: prompts.filter((p) => p.mine || p.shared) });
  }
  if (path === '/prompts' && method === 'POST') {
    if (role === 'viewer') return json({ error: 'forbidden' }, 403);
    const p = input as { title?: string; body?: string; shared?: boolean };
    if (!p.title?.trim() || !p.body?.trim()) {
      return json({ error: 'invalid_request', message: 'A prompt needs a title and a text.' }, 400);
    }
    const now = Date.now();
    const prompt: AiPrompt = {
      id: `prompt-${nextPrompt++}`,
      title: p.title.trim(),
      body: p.body.trim(),
      shared: p.shared === true,
      createdBy: 'mock-user',
      createdAt: now,
      updatedAt: now,
      mine: true,
    };
    prompts = [prompt, ...prompts];
    return json({ prompt }, 201);
  }
  const promptMatch = /^\/prompts\/([^/]+)$/.exec(path);
  if (promptMatch) {
    const id = promptMatch[1];
    const current = prompts.find((p) => p.id === id);
    if (role === 'viewer') return json({ error: 'forbidden' }, 403);
    if (!current || (!current.mine && role !== 'admin')) return json({ error: 'not_found' }, 404);
    if (method === 'DELETE') {
      prompts = prompts.filter((p) => p.id !== id);
      return json({ ok: true });
    }
    if (method === 'PUT') {
      const p = input as { title?: string; body?: string; shared?: boolean };
      if (!p.title?.trim() || !p.body?.trim()) {
        return json(
          { error: 'invalid_request', message: 'A prompt needs a title and a text.' },
          400,
        );
      }
      prompts = prompts.map((x) =>
        x.id === id
          ? {
              ...x,
              title: p.title!.trim(),
              body: p.body!.trim(),
              shared: p.shared === true,
              updatedAt: Date.now(),
            }
          : x,
      );
      return json({ ok: true });
    }
  }

  // --- everything below needs editor+ and passes the gate ---
  if (method !== 'POST') return null;
  if (role === 'viewer') return json({ error: 'forbidden' }, 403);
  const refused = gate();
  if (refused) return refused;

  if (path === '/chat') {
    return json({
      reply: 'Mock answer: open Campaigns, pick a template and run the pre-send check.',
    });
  }
  if (path === '/skills/subject-lines') {
    const languages = (input.languages as AiLanguage[] | undefined) ?? ['en'];
    const suggestions: AiSubjectSuggestion[] = languages
      .filter((l) => RESPONSE_LANGUAGES.includes(l))
      .flatMap((language) =>
        [1, 2, 3].map((n) => ({
          language,
          subject: `Mock subject ${n} (${language})`,
          preheader: `Mock preheader ${n}`,
        })),
      );
    return json({ suggestions });
  }
  if (path === '/skills/newsletter') {
    const id = `tpl-ai-${nextTemplate++}`;
    return json(
      {
        template: { id, name: (input.name as string | undefined) ?? 'AI draft: Mock newsletter' },
        subject: 'Mock newsletter subject',
        preheader: 'Mock preheader',
        reference: { id: 'cmp-sent-1', name: 'Halloween 2026 (EN)' },
        keptTexts: 0,
      },
      201,
    );
  }
  if (path === '/skills/translate') {
    const languages = (input.languages as AiLanguage[] | undefined) ?? [];
    return json(
      {
        created: languages.map((language) => ({
          language,
          template: { id: `tpl-ai-${nextTemplate++}`, name: `Mock template (${language})` },
        })),
        failed: [],
      },
      201,
    );
  }
  if (path === '/skills/segment') {
    return json({
      name: 'Danish contacts',
      filter: { version: 2, groups: [{ languages: ['da'] }] },
    });
  }
  if (path === '/skills/campaign-summary') {
    return json({
      summary: 'Mock summary: the campaign was delivered to nearly everyone and a few clicked.',
    });
  }
  return null;
}
